import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import {
  Button,
  Callout,
  Card,
  Field,
  Input,
  Loading,
  PageHeader,
  Select,
  Textarea,
  Toggle,
} from '../components/ui/primitives';
import type { ClientRecord } from '../lib/types';

const SAMPLE_HTML = `<p>Hi {{first_name}},</p>

<p>I work with operations teams at companies like {{company}} on reducing
procurement cycle time. We usually find two or three weeks of slack in the
approval chain.</p>

<p>Worth a short call to see whether the same applies at {{company}}?</p>

<p><a href="https://example.com/book-a-call">Book 15 minutes</a></p>

<p>Best,<br>Sarah</p>`;

export function CampaignNewPage() {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState({
    clientId: '',
    name: '',
    subject: '',
    preheader: '',
    htmlBody: SAMPLE_HTML,
    textBody: '',
    scheduleAt: '',
    listIds: [] as string[],
    senderIdentityIds: [] as string[],
    allowRole: false,
    allowCatchAll: false,
    allowUnverified: true,
  });

  const { data: clients } = useQuery({
    queryKey: ['clients'],
    queryFn: () => api.get<{ clients: ClientRecord[] }>('/api/clients'),
  });

  const clientId = form.clientId || clients?.clients[0]?.id || '';

  const { data: lists } = useQuery({
    queryKey: ['lists', clientId],
    queryFn: () =>
      api.get<{ lists: Array<{ id: string; name: string; contactCount: number }> }>(
        '/api/clients/' + clientId + '/lists'
      ),
    enabled: Boolean(clientId),
  });

  const { data: identities } = useQuery({
    queryKey: ['identities'],
    queryFn: () =>
      api.get<{ identities: Array<{ id: string; email: string; status: string }> }>('/api/identities'),
  });

  const create = useMutation({
    mutationFn: () =>
      api.post<{ id: string }>('/api/campaigns', {
        clientId,
        name: form.name,
        subject: form.subject,
        preheader: form.preheader || null,
        htmlBody: form.htmlBody,
        textBody: form.textBody || null,
        scheduleAt: form.scheduleAt ? new Date(form.scheduleAt).toISOString() : null,
        listIds: form.listIds,
        senderIdentityIds: form.senderIdentityIds,
        audienceRules: {
          allowRole: form.allowRole,
          allowCatchAll: form.allowCatchAll,
          allowUnverified: form.allowUnverified,
        },
      }),
    onSuccess: (campaign) => navigate('/campaigns/' + campaign.id),
    onError: (err: Error) => setError(err.message),
  });

  if (!clients) return <Loading />;

  const toggle = (key: 'listIds' | 'senderIdentityIds', id: string) => {
    setForm((prev) => ({
      ...prev,
      [key]: prev[key].includes(id) ? prev[key].filter((v) => v !== id) : [...prev[key], id],
    }));
  };

  return (
    <>
      <PageHeader
        title="New campaign"
        description="Nothing sends on save. The campaign is created as a draft so you can review the compliance check and the send plan first."
      />

      <div className="grid gap-5 lg:grid-cols-3">
        <div className="space-y-5 lg:col-span-2">
          <Card title="Message">
            <div className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Client">
                  <Select value={clientId} onChange={(e) => setForm({ ...form, clientId: e.target.value })}>
                    {clients.clients.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </Select>
                </Field>
                <Field label="Campaign name">
                  <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                </Field>
              </div>

              <Field label="Subject" hint="Merge fields work here too: {{first_name}}, {{company}}.">
                <Input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} />
              </Field>

              <Field label="Preheader">
                <Input value={form.preheader} onChange={(e) => setForm({ ...form, preheader: e.target.value })} />
              </Field>

              <Field
                label="HTML body"
                hint="Links are rewritten through our click endpoint so every click passes the bot filter. The unsubscribe footer and postal address are appended automatically."
              >
                <Textarea
                  rows={16}
                  value={form.htmlBody}
                  onChange={(e) => setForm({ ...form, htmlBody: e.target.value })}
                />
              </Field>

              <Field label="Plain text alternative" hint="Strongly recommended - HTML-only mail scores worse.">
                <Textarea
                  rows={6}
                  value={form.textBody}
                  onChange={(e) => setForm({ ...form, textBody: e.target.value })}
                />
              </Field>
            </div>
          </Card>
        </div>

        <div className="space-y-5">
          <Card title="Audience">
            <div className="space-y-2">
              {(lists?.lists ?? []).map((l) => (
                <label key={l.id} className="flex items-center gap-2 text-sm text-ink-700">
                  <input
                    type="checkbox"
                    checked={form.listIds.includes(l.id)}
                    onChange={() => toggle('listIds', l.id)}
                  />
                  {l.name}
                  <span className="text-xs text-ink-400">({l.contactCount})</span>
                </label>
              ))}
              {!lists?.lists.length && <p className="text-xs text-ink-500">This client has no lists yet.</p>}
            </div>

            <div className="mt-4 space-y-2.5 border-t border-ink-100 pt-4">
              <Toggle
                checked={form.allowUnverified}
                onChange={(v) => setForm({ ...form, allowUnverified: v })}
                label="Include unverified addresses"
              />
              <Toggle
                checked={form.allowRole}
                onChange={(v) => setForm({ ...form, allowRole: v })}
                label="Include role addresses (info@, sales@)"
              />
              <Toggle
                checked={form.allowCatchAll}
                onChange={(v) => setForm({ ...form, allowCatchAll: v })}
                label="Include catch-all domains"
              />
            </div>
          </Card>

          <Card title="Sending mailboxes" subtitle="The campaign rotates across these, respecting each one's caps.">
            <div className="space-y-2">
              {(identities?.identities ?? []).map((i) => (
                <label key={i.id} className="flex items-center gap-2 text-sm text-ink-700">
                  <input
                    type="checkbox"
                    checked={form.senderIdentityIds.includes(i.id)}
                    onChange={() => toggle('senderIdentityIds', i.id)}
                  />
                  <span className="font-mono text-xs">{i.email}</span>
                  <span className="text-xs text-ink-400">{i.status}</span>
                </label>
              ))}
              {!identities?.identities.length && (
                <p className="text-xs text-ink-500">Add a mailbox on a sending domain first.</p>
              )}
            </div>
          </Card>

          <Card title="Schedule">
            <Field label="Start at" hint="Leave blank to start as soon as it is launched. Sends are clamped to the send window.">
              <Input
                type="datetime-local"
                value={form.scheduleAt}
                onChange={(e) => setForm({ ...form, scheduleAt: e.target.value })}
              />
            </Field>
          </Card>

          {error && <Callout tone="bad">{error}</Callout>}

          <Button
            variant="primary"
            className="w-full"
            loading={create.isPending}
            disabled={!form.name || !form.subject || !form.listIds.length || !form.senderIdentityIds.length}
            onClick={() => create.mutate()}
          >
            Create draft
          </Button>
        </div>
      </div>
    </>
  );
}
